import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  onClose?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class DirectorErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[DirectorErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const isWebGLError = this.state.error?.message?.includes('WebGL') || false;
      return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#09090b]">
          <div className="flex flex-col items-center gap-5 p-8 max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
              <AlertTriangle size={28} className="text-red-400" />
            </div>
            <div>
              <h3 className="text-base font-medium text-zinc-200 mb-2">
                {isWebGLError ? 'WebGL 渲染失败' : '导演台发生错误'}
              </h3>
              <p className="text-sm text-zinc-500 leading-relaxed">
                {isWebGLError
                  ? '你的浏览器或设备不支持 WebGL，或者显存不足。请尝试更新显卡驱动、关闭其他 3D 应用，或更换浏览器。'
                  : '导演台遇到了意外错误，你可以尝试重新加载。'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={this.handleReload}
                className="px-5 py-2 text-sm font-medium text-zinc-200 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700/60 rounded-lg transition-colors"
              >
                重新加载
              </button>
              {this.props.onClose && (
                <button
                  onClick={this.props.onClose}
                  className="px-5 py-2 text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  关闭
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
